import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { streamLainDisclosureMotion } from '@blocksuite/affine-shared/utils';
import {
  ArrowDownSmallIcon,
  MoreHorizontalIcon,
  PlusIcon,
  ViewIcon,
} from '@blocksuite/icons/lit';
import { html, nothing, type TemplateResult } from 'lit';

import { type DataViewI18nKey, dataViewI18nT } from '../../../core/i18n.js';
import {
  renderTemporalModeGroup,
  renderTemporalRangeControls,
  TEMPORAL_TOOLBAR_ICON_SIZE,
} from '../../shared/toolbar/temporal-toolbar.js';
import type { CalendarMode } from '../define.js';
import {
  animateCalendarSourcePopup,
  calendarFeedbackMotion,
} from './calendar-chrome-motion.js';
import type {
  CalendarDisplayProperty,
  CalendarDisplaySettings,
} from './calendar-display-policy.js';
import { styles } from './calendar-view-styles.js';
import { renderCalendarRangeTitle } from './toolbar/calendar-toolbar.js';

export type CalendarHost = 'planner' | 'canvas' | 'fullscreen' | 'page';
export const CALENDAR_FUNCTION_IDS = [
  'create',
  'export-ics',
  'import-ics',
  'copy-feed',
  'add-feed',
  'refresh-feed',
  'remove-feed',
  'toggle-feed',
  'mailbox-status',
  'filter',
  'sort',
  'search',
  'setup',
  'complete-fields',
  'rename-source',
  'source-views',
] as const;
export type CalendarFunctionId = (typeof CALENDAR_FUNCTION_IDS)[number];
/**
 * Functions kept in the state contract but not offered in the menu. Feed subscriptions
 * are fetched directly by the browser, so most providers fail without CORS headers;
 * adding feeds stays hidden until feeds are fetched server-side.
 */
export const HIDDEN_CALENDAR_FUNCTIONS: ReadonlySet<CalendarFunctionId> =
  new Set(['add-feed']);
export interface CalendarFunctionState {
  readonly id: CalendarFunctionId;
  readonly enabled: boolean;
  readonly pending: boolean;
  readonly disabledReason: string | null;
}
export interface CalendarChromeState {
  readonly host: CalendarHost;
  readonly canConfigureView: boolean;
  readonly mode: CalendarMode;
  readonly cursor: Date;
  readonly display: CalendarDisplaySettings;
  readonly selectedSourceId: string | null;
  readonly sourceIdentityCount: number;
  readonly functions: readonly CalendarFunctionState[];
}
export interface CalendarChromeProps {
  readonly state: CalendarChromeState;
  readonly sourceTools: TemplateResult | typeof nothing;
  readonly hostControls: TemplateResult | typeof nothing;
  readonly onToday: () => void;
  readonly onPrevious: () => void;
  readonly onNext: () => void;
  readonly onModeChange: (mode: CalendarMode) => void;
  readonly onDisplayChange: (settings: CalendarDisplaySettings) => void;
  readonly onFunction: (id: CalendarFunctionId) => void;
}

const FUNCTION_LABELS: Record<CalendarFunctionId, DataViewI18nKey> = {
  create: 'calendar.action.new',
  'export-ics': 'calendar.action.export',
  'import-ics': 'calendar.action.import',
  'copy-feed': 'calendar.action.copyFeed',
  'add-feed': 'calendar.action.addFeed',
  'refresh-feed': 'calendar.chrome.refreshFeed',
  'remove-feed': 'calendar.chrome.removeFeed',
  'toggle-feed': 'calendar.chrome.toggleFeed',
  'mailbox-status': 'calendar.mailboxLink.title',
  filter: 'calendar.chrome.filter',
  sort: 'calendar.chrome.sort',
  search: 'calendar.chrome.search',
  setup: 'calendar.empty.setupCta',
  'complete-fields': 'calendar.action.completeFields',
  'rename-source': 'calendar.chrome.renameSource',
  'source-views': 'calendar.chrome.sourceViews',
};

/** Host-independent catalog: permissions and availability come from the real source. */
export function renderCalendarChrome(
  props: CalendarChromeProps
): TemplateResult {
  const { state } = props;
  const actions = new Map(state.functions.map(fn => [fn.id, fn]));
  if (
    actions.size !== CALENDAR_FUNCTION_IDS.length ||
    CALENDAR_FUNCTION_IDS.some(id => !actions.has(id))
  ) {
    throw new Error('Incomplete calendar function state');
  }
  const action = (id: CalendarFunctionId) => {
    const fn = actions.get(id)!;
    return html`<button
      class=${id === 'create' ? styles.primaryButton : styles.menuItem}
      role=${id === 'create' ? nothing : 'menuitem'}
      aria-label=${dataViewI18nT(FUNCTION_LABELS[id])}
      data-calendar-function=${id}
      ?disabled=${!fn.enabled || fn.pending}
      aria-busy=${String(fn.pending)}
      title=${fn.disabledReason ?? ''}
      @click=${(event: MouseEvent) => {
        if (!fn.enabled || fn.pending) return;
        const details = (event.currentTarget as HTMLElement).closest('details');
        const summary = details?.querySelector<HTMLElement>(':scope > summary');
        if (summary?.getAttribute('aria-expanded') === 'true') summary.click();
        props.onFunction(id);
      }}
    >
      ${id === 'create'
        ? html`<span aria-hidden="true"
            >${PlusIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}</span
          >`
        : nothing}
      <span data-calendar-action-label
        >${dataViewI18nT(FUNCTION_LABELS[id])}</span
      >
    </button>`;
  };
  return html`<div
    ${calendarFeedbackMotion()}
    class=${styles.chromeHost}
    @keydown=${(event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented)
        return;
      const details = (event.target as Element).closest<HTMLDetailsElement>(
        '[data-calendar-display-menu],[data-calendar-more-menu]'
      );
      const summary = details?.querySelector<HTMLElement>(':scope > summary');
      if (summary?.getAttribute('aria-expanded') === 'true') {
        event.preventDefault();
        event.stopPropagation();
        summary.click();
        summary.focus();
      }
    }}
  >
    <div
      class=${styles.toolbar}
      data-calendar-toolbar
      data-calendar-chrome=${state.host}
    >
      <div class=${styles.leadingGroup} data-calendar-chrome-leading>
        ${renderTemporalModeGroup<CalendarMode>({
          activeMode: state.mode,
          buttonClass: styles.modeButton,
          groupClass: styles.modeGroup,
          onModeChange: props.onModeChange,
          options: (['day', 'week', 'month'] as const).map(mode => ({
            key: mode,
            label: dataViewI18nT(`calendar.mode.${mode}`),
            disabled: !state.canConfigureView,
            ariaLabel: dataViewI18nT('calendar.accessibility.switchMode', {
              mode: dataViewI18nT(`calendar.mode.${mode}`),
            }),
          })),
        })}
        <div class=${styles.rangeTitle} data-temporal-range-part="title">
          ${renderCalendarRangeTitle(state.mode, state.cursor)}
        </div>
      </div>
      <div class=${styles.navGroup} data-calendar-chrome-pager>
        ${renderTemporalRangeControls({
          buttonClass: styles.todayButton,
          groupClass: styles.modeGroup,
          iconButtonClass: styles.iconButton,
          nextLabel: dataViewI18nT('calendar.accessibility.nextRange'),
          previousLabel: dataViewI18nT('calendar.accessibility.previousRange'),
          todayLabel: dataViewI18nT('calendar.action.today'),
          todayAriaLabel: dataViewI18nT('calendar.accessibility.goToday'),
          onNext: props.onNext,
          onPrevious: props.onPrevious,
          onToday: props.onToday,
        })}
      </div>
      <div class=${styles.actionGroup} data-calendar-chrome-actions>
        ${action('create')}
        <details
          ${streamLainDisclosureMotion()}
          class=${styles.moreMenu}
          data-calendar-display-menu
        >
          <summary
            class=${styles.todayButton}
            aria-label=${dataViewI18nT('calendar.chrome.display')}
          >
            <span aria-hidden="true"
              >${ViewIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}</span
            >
            <span data-calendar-action-label
              >${dataViewI18nT('calendar.chrome.display')}</span
            >
          </summary>
          <div class="${styles.menuPanel} streamlain-inline-disclosure-body">
            ${renderDisplay(props)}
          </div>
        </details>
        <details
          ${streamLainDisclosureMotion()}
          class=${styles.moreMenu}
          data-calendar-more-menu
        >
          <summary
            class=${styles.todayButton}
            aria-label=${dataViewI18nT('calendar.accessibility.moreActions')}
            aria-haspopup="menu"
          >
            <span aria-hidden="true"
              >${MoreHorizontalIcon(TEMPORAL_TOOLBAR_ICON_SIZE)}</span
            >
            <span data-calendar-action-label
              >${dataViewI18nT('calendar.action.more')}</span
            >
          </summary>
          <div class="${styles.menuPanel} streamlain-inline-disclosure-body">
            ${props.sourceTools === nothing
              ? nothing
              : html`<div class=${styles.menuSource} data-calendar-menu-source>
                  ${props.sourceTools}
                </div>`}
            <div
              role="menu"
              aria-label=${dataViewI18nT('calendar.accessibility.moreActions')}
            >
              ${(
                [
                  ['filter', 'sort', 'search'],
                  ['export-ics', 'import-ics', 'copy-feed'],
                  ['add-feed', 'refresh-feed', 'toggle-feed', 'remove-feed'],
                  [
                    'mailbox-status',
                    'setup',
                    'complete-fields',
                    'rename-source',
                    'source-views',
                  ],
                ] satisfies CalendarFunctionId[][]
              ).map(
                group =>
                  html`<div
                    class=${styles.menuGroup}
                    role="group"
                    data-calendar-menu-group
                  >
                    ${group
                      .filter(id => !HIDDEN_CALENDAR_FUNCTIONS.has(id))
                      .map(action)}
                  </div>`
              )}
            </div>
          </div>
        </details>
        ${props.hostControls}
      </div>
    </div>
  </div>`;
}

/** Uses the shared menu renderer and its keyboard/focus lifecycle in every host. */
export function renderCalendarChromeSelect(
  label: string,
  value: string,
  options: readonly { value: string; label: string }[],
  onChange: (value: string) => void,
  disabled = false
): TemplateResult {
  return html`<button
    type="button"
    class=${styles.sourceSelectButton}
    aria-label=${label}
    aria-haspopup="menu"
    ?disabled=${disabled}
    @click=${(event: MouseEvent) => {
      if (disabled) return;
      animateCalendarSourcePopup(
        popMenu(popupTargetFromElement(event.currentTarget as HTMLElement), {
          options: {
            items: options.map(option =>
              menu.action({
                name: option.label,
                isSelected: option.value === value,
                select: () => onChange(option.value),
              })
            ),
          },
        })
      );
    }}
  >
    <span
      >${options.find(option => option.value === value)?.label ?? label}</span
    >
    <span aria-hidden="true">${ArrowDownSmallIcon()}</span>
  </button>`;
}

const DISPLAY_PROPERTIES: readonly CalendarDisplayProperty[] = [
  'location',
  'attendees',
  'status',
];
function renderDisplay(props: CalendarChromeProps): TemplateResult {
  const { display, mode } = props.state;
  const selected = display.properties[mode];
  const limit = mode === 'day' ? 2 : mode === 'week' ? 1 : 0;
  const commit = (properties: readonly CalendarDisplayProperty[]) => {
    if (!props.state.canConfigureView) return;
    props.onDisplayChange({
      ...display,
      properties: { ...display.properties, [mode]: properties },
    });
  };
  return html`
    ${props.state.sourceIdentityCount > 1
      ? html`<div class=${styles.menuField}>
          <span>${dataViewI18nT('calendar.detail.calendar')}</span>
          ${renderCalendarChromeSelect(
            dataViewI18nT('calendar.detail.calendar'),
            display.sourceIdentityMode,
            (['auto', 'always', 'never'] as const).map(value => ({
              value,
              label: dataViewI18nT(`calendar.chrome.identity.${value}`),
            })),
            value =>
              props.onDisplayChange({
                ...display,
                sourceIdentityMode:
                  value as CalendarDisplaySettings['sourceIdentityMode'],
              }),
            !props.state.canConfigureView
          )}
        </div>`
      : nothing}
    ${limit === 0
      ? html`<span>${dataViewI18nT('calendar.chrome.monthCompact')}</span>`
      : DISPLAY_PROPERTIES.map(
          property =>
            html`<div class=${styles.menuField}>
              <label
                ><input
                  type="checkbox"
                  data-calendar-display-property=${property}
                  .checked=${selected.includes(property)}
                  ?disabled=${!props.state.canConfigureView}
                  @change=${(event: Event) => {
                    const values = selected.filter(value => value !== property);
                    commit(
                      (event.currentTarget as HTMLInputElement).checked
                        ? [...values, property].slice(-limit)
                        : values
                    );
                  }}
                />${dataViewI18nT(
                  `calendar.chrome.property.${property}`
                )}</label
              >
              ${selected.includes(property) && selected.length > 1
                ? ([-1, 1] as const).map(
                    direction =>
                      html`<button
                        class=${styles.iconButton}
                        aria-label=${dataViewI18nT(
                          direction === -1
                            ? 'calendar.chrome.moveUp'
                            : 'calendar.chrome.moveDown'
                        )}
                        ?disabled=${!props.state.canConfigureView ||
                        selected.indexOf(property) + direction < 0 ||
                        selected.indexOf(property) + direction >=
                          selected.length}
                        @click=${() => {
                          const values = [...selected];
                          const index = values.indexOf(property);
                          const to = index + direction;
                          if (to >= 0 && to < values.length) {
                            values.splice(index, 1);
                            values.splice(to, 0, property);
                            commit(values);
                          }
                        }}
                      >
                        ${direction === -1 ? '↑' : '↓'}
                      </button>`
                  )
                : nothing}
            </div>`
        )}
  `;
}
